import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { showErrorToast, showSuccessToast } from '../../util/toast';
import { ListingType, TRANSACTION_PROCESS_ALIAS, UNIT_TYPE } from '../../util/enums';
import { EDIT, REWARD } from '../../util/constants';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isArrayLength } from '../../util/genericHelpers';
import { uploadImages } from '../EditListingPage/EditListingPage.duck';
import moment from 'moment';
import { processArrayField } from '../../util/editListingHelpers';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const CREATE_REWARD_LISTING_REQUEST = 'app/RewardsPage/CREATE_REWARD_LISTING_REQUEST';
export const CREATE_REWARD_LISTING_SUCCESS = 'app/RewardsPage/CREATE_REWARD_LISTING_SUCCESS';
export const CREATE_REWARD_LISTING_ERROR = 'app/RewardsPage/CREATE_REWARD_LISTING_ERROR';

export const SHOW_LISTING_REQUEST = 'app/RewardsPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/RewardsPage/SHOW_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  createRewardListingInProgress: false,
  createRewardListingError: null,
  id: null,
  showListingError: null,
};

const RewardsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CREATE_REWARD_LISTING_REQUEST:
      return { ...state, createRewardListingInProgress: true, createRewardListingError: null };
    case CREATE_REWARD_LISTING_SUCCESS:
      return { ...state, createRewardListingInProgress: false, createRewardListingError: null };
    case CREATE_REWARD_LISTING_ERROR:
      return {
        ...state,
        createRewardListingInProgress: false,
        createRewardListingError: payload,
      };
    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };
    default:
      return state;
  }
};

export default RewardsPageReducer;

// ================ Selectors ================ //
export const createRewardListingSelector = state => {
  const { createRewardListingInProgress, createRewardListingError } = state.PerksPostPage;
  return {
    createRewardListingInProgress,
    createRewardListingError,
  };
};

// ================ Action creators ================ //

export const createRewardListingRequest = () => ({ type: CREATE_REWARD_LISTING_REQUEST });
export const createRewardListingSuccess = () => ({
  type: CREATE_REWARD_LISTING_SUCCESS,
});
export const createRewardListingError = error => ({
  type: CREATE_REWARD_LISTING_ERROR,
  error: true,
  payload: error,
});
export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const createRewardListing = params => async (dispatch, getState, sdk) => {
  dispatch(createRewardListingRequest());

  try {
    const {
      title,
      description,
      userType,
      rewardExpirationDate,
      listingImageConfig,
      codeImage,
      parentListingTitle,
      parentListingId,
      pub_sports,
      ...rest
    } = params || {};
    const updatedValues = {
      title: title.trim(),
      description: description.trim(),
      publicData: {
        transactionProcessAlias: TRANSACTION_PROCESS_ALIAS.DEFAULT_PURCHASE,
        unitType: UNIT_TYPE.ITEM,
        listingType: ListingType.PURCHASE,
        listingCategory: `${userType}${REWARD}`,
        rewardExpirationDate: moment(rewardExpirationDate).unix(),
        ...(isArrayLength(pub_sports) && {
          pub_sports: processArrayField(pub_sports),
        }),
        ...(parentListingId && { parentListingId }),
        ...(parentListingTitle && { parentListingTitle }),
        ...rest,
      },
    };
    if (params.id) {
      updatedValues.id = new UUID(params.id);
    }
    if (isArrayLength(codeImage)) {
      const uploadedImageResponse = await dispatch(
        uploadImages(codeImage, listingImageConfig, false)
      );
      const imageIds =
        (isArrayLength(uploadedImageResponse) && uploadedImageResponse.map(img => img?.imageId)) ||
        null;
      if (imageIds) {
        updatedValues.images = imageIds;
      }
    }
    const response = params.id
      ? await sdk.ownListings.update(updatedValues, { expand: true })
      : await sdk.ownListings.create(updatedValues, { expand: true });
    const listing = response?.data?.data;
    const listingId = listing?.id?.uuid;

    if (listingId) {
      await sdk.stockAdjustments.create(
        {
          listingId,
          quantity: 1,
        },
        {
          expand: true,
          include: ['ownListing.currentStock'],
        }
      );
    }
    dispatch(createRewardListingSuccess());
    showSuccessToast('Reward created successfully');
    return listing;
  } catch (error) {
    dispatch(createRewardListingError(storableError(error)));
    showErrorToast('Reward creation failed');
  }
};
export const showListing = (listingId, config) => async (dispatch, getState, sdk) => {
  // Destructure layout config with defaults
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Dispatch initial actions
  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());

  // Build the query parameters
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'author.publicData', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      // Social media
      'variants.facebook',
      'variants.twitter',
      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  try {
    const data = await sdk.ownListings.show(params);
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };
    dispatch(addMarketplaceEntities(data, sanitizeConfig));
    return data;
  } catch (error) {
    dispatch(showListingError(storableError(error)));
  }
};
export const loadData = (params, search, config) => dispatch => {
  const listingId = new UUID(params.id);
  const type = params?.type;
  if (type === EDIT) {
    dispatch(showListing(listingId, config));
  }
  return dispatch(fetchCurrentUser());
};
